import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Text,
  useColorModeValue,
  Flex,
  HStack,
  Badge,
  // eslint-disable-next-line no-restricted-imports
  IconButton,
  Input,
  Menu,
  // eslint-disable-next-line no-restricted-imports
  MenuButton,
  // eslint-disable-next-line no-restricted-imports
  MenuList,
  // eslint-disable-next-line no-restricted-imports
  MenuItem,
  Spinner,
} from '@chakra-ui/react';
import { captureException } from '@sentry/react';
import { Icon } from 'src/components/atoms/Icon';
import {
  Channel,
  ConnectionAction,
  SERVICES_REQUIRING_ACCOUNT_SELECTION,
  SERVICES_SUPPORTING_CAMPAIGN_FILTER,
} from 'src/features/Settings/utils/constants';
import { useAppContext } from 'src/contexts/AppContext';
import { useOauthAuthenticationUrlQuery } from 'src/api/queries';
import { ShopDomainModal } from 'src/features/Settings/components/molecules/ShopDomainModal';

type ConnectionItemProps = {
  channel: Channel;
  onConnectionFailure?: (channelName: string) => void;
  onDisconnect?: (channel: Channel) => void;
  onSelectAccounts?: (channel: Channel) => void;
  onCampaignFilter?: (channel: Channel) => void;
  isLoading?: boolean;
};

const ConnectionItem: React.FC<ConnectionItemProps> = ({
  channel,
  onConnectionFailure,
  onDisconnect,
  onSelectAccounts,
  onCampaignFilter,
  isLoading = false,
}) => {
  const textColor = useColorModeValue('gray.600', 'gray.300');
  const [isEditing, setIsEditing] = useState(false);
  const [editedUsername, setEditedUsername] = useState(channel.accountName);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isShopDomainOpen, setIsShopDomainOpen] = useState(false);
  const [shopDomain, setShopDomain] = useState<string | undefined>(undefined);
  const [isShopConnectPending, setIsShopConnectPending] = useState(false);
  const { currentCustomer } = useAppContext();
  const customerName = currentCustomer?.customerName ?? '';

  const { refetch: fetchOauthUrl } = useOauthAuthenticationUrlQuery({
    customer: customerName,
    service: channel.service,
    shop_domain: shopDomain,
  });

  useEffect(() => {
    setEditedUsername(channel.accountName);
    if (channel.status !== 'connected') {
      setIsEditing(false);
    }
  }, [channel.accountName, channel.status]);

  const startOauthFlow = useCallback(async () => {
    if (!customerName) {
      onConnectionFailure?.(channel.name);
      return;
    }

    setIsConnecting(true);
    try {
      const response = await fetchOauthUrl();
      const authorizationUrl = response.data?.authorizationUrl;
      if (!authorizationUrl) {
        throw new Error(
          `Missing authorization url for integration ${channel.service}`,
        );
      }
      window.open(authorizationUrl, 'oauth', 'width=500,height=700');
    } catch (error) {
      captureException({ error, fn: 'ConnectionItem.handleConnectionAttempt' });
      onConnectionFailure?.(channel.name);
    } finally {
      setIsConnecting(false);
    }
  }, [
    channel.name,
    channel.service,
    customerName,
    fetchOauthUrl,
    onConnectionFailure,
  ]);

  const handleConnectionAttempt = useCallback(() => {
    if (isConnecting || isLoading) {
      return;
    }

    // Shopify's OAuth flow runs on the shop's own domain, so the store
    // address must be collected before the popup can be opened
    if (channel.service === 'shopify') {
      setIsShopDomainOpen(true);
      return;
    }

    void startOauthFlow();
  }, [channel.service, isConnecting, isLoading, startOauthFlow]);

  const handleShopDomainConnect = useCallback((selectedShopDomain: string) => {
    // The OAuth flow starts from the effect below, once the query hook has
    // re-rendered with the new shop domain (refetch has no way to take params)
    setShopDomain(selectedShopDomain);
    setIsShopDomainOpen(false);
    setIsShopConnectPending(true);
  }, []);

  useEffect(() => {
    if (!isShopConnectPending || !shopDomain) return;

    setIsShopConnectPending(false);
    void startOauthFlow();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isShopConnectPending, shopDomain]);

  const handleSave = () => {
    setIsEditing(false);
  };

  const handleDisconnect = () => {
    setIsEditing(false);
    setEditedUsername('');
    onDisconnect?.(channel);
  };

  const handleReconnect = () => {
    handleConnectionAttempt();
  };

  const handleSelectAccounts = () => {
    onSelectAccounts?.(channel);
  };

  const handleCampaignFilter = () => {
    onCampaignFilter?.(channel);
  };

  const showAccountSelection = SERVICES_REQUIRING_ACCOUNT_SELECTION.includes(
    channel.service,
  );

  const showCampaignFilter = SERVICES_SUPPORTING_CAMPAIGN_FILTER.includes(
    channel.service,
  );

  const getRightSideContent = () => {
    const isBusy = isConnecting || isLoading;

    switch (channel.status) {
      case 'connected':
        return (
          <Flex align="center">
            <Flex align="center" px={3}>
              <Box w={2} h={2} bg="icon.green" borderRadius="full" mr={2} />
              <Text fontSize="sm" color="icon.green" fontWeight="normal">
                Connected
              </Text>
            </Flex>
          </Flex>
        );
      case 'failed':
        return null;
      default:
        return (
          <IconButton
            aria-label="connect"
            icon={
              isBusy ? (
                <Spinner size="sm" color="text.title" />
              ) : (
                <Icon iconName="chevron-right" boxSize={5} color="text.title" />
              )
            }
            size="sm"
            variant="ghost"
            _hover={{ bg: 'surface.hover' }}
            onClick={handleConnectionAttempt}
            isDisabled={isBusy}
          />
        );
    }
  };

  const getActionButtons = () => {
    const isBusy = isConnecting || isLoading;

    if (channel.status === 'not-connected') return null;

    return (
      <HStack spacing={2} ml={4}>
        {channel.actions.map((action: ConnectionAction, index: number) => {
          switch (action) {
            case 'edit':
              return (
                <Menu key={index}>
                  <MenuButton
                    as={IconButton}
                    aria-label="edit"
                    icon={
                      <Icon iconName="pencil" boxSize={5} color="text.title" />
                    }
                    size="sm"
                    variant="ghost"
                    _hover={{ bg: 'surface.hover' }}
                    isDisabled={isBusy}
                  />
                  <MenuList>
                    <MenuItem onClick={handleSave} isDisabled={isBusy}>
                      <Icon iconName="check" boxSize={4} mr={2} />
                      Save changes
                    </MenuItem>
                    {showAccountSelection && (
                      <MenuItem
                        onClick={handleSelectAccounts}
                        isDisabled={isBusy}>
                        <Icon iconName="settings" boxSize={4} mr={2} />
                        Select accounts
                      </MenuItem>
                    )}
                    {showCampaignFilter && (
                      <MenuItem
                        onClick={handleCampaignFilter}
                        isDisabled={isBusy}>
                        <Icon iconName="filters" boxSize={4} mr={2} />
                        Campaign name filter
                      </MenuItem>
                    )}
                    <MenuItem
                      onClick={handleDisconnect}
                      color="red.500"
                      isDisabled={isBusy}>
                      <Icon iconName="close" boxSize={4} mr={2} />
                      Disconnect
                    </MenuItem>
                  </MenuList>
                </Menu>
              );
            case 'reconnect':
              return (
                <IconButton
                  key={index}
                  aria-label="reconnect"
                  icon={
                    isBusy ? (
                      <Spinner size="sm" color="text.title" />
                    ) : (
                      <Icon iconName="refresh" boxSize={5} color="text.title" />
                    )
                  }
                  size="sm"
                  variant="ghost"
                  color="gray.500"
                  _hover={{ bg: 'surface.hover' }}
                  onClick={handleReconnect}
                  isDisabled={isBusy}
                />
              );
            default:
              return null;
          }
        })}
      </HStack>
    );
  };

  const renderFieldContent = () => {
    if (channel.status === 'not-connected') {
      return (
        <Text
          fontSize="sm"
          color={textColor}
          noOfLines={1}
          wordBreak="break-all">
          {channel.placeholder || 'Write a username here'}
        </Text>
      );
    }

    if (isEditing) {
      return (
        <Input
          value={editedUsername}
          onChange={(e) => setEditedUsername(e.target.value)}
          size="sm"
          fontSize="sm"
          placeholder="Enter username"
          onBlur={() => setIsEditing(false)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              handleSave();
            } else if (e.key === 'Escape') {
              setIsEditing(false);
              setEditedUsername(channel.accountName);
            }
          }}
          autoFocus
        />
      );
    }

    return (
      <Text
        fontSize="sm"
        color={textColor}
        noOfLines={1}
        wordBreak="break-all"
        cursor="pointer"
        onClick={() => setIsEditing(true)}>
        {channel.accountName}
      </Text>
    );
  };

  return (
    <Box mb={2} minWidth={0} key={channel.id}>
      {/* Channel name and status badge above the card */}
      <Flex
        justify="space-between"
        align={{ base: 'flex-start', md: 'center' }}
        mb={2}
        minHeight={{ base: 'auto', md: '18px' }}
        direction={{ base: 'column', md: 'row' }}
        gap={{ base: 1, md: 0 }}>
        <Text fontSize="sm" color={textColor} fontWeight="normal">
          {channel.name}
        </Text>
        {channel.status === 'connected' &&
          showAccountSelection &&
          channel.selectedAccounts === null && (
            <Badge
              fontSize={{ base: '2xs', md: 'xs' }}
              bg="orange.100"
              color="orange.700"
              borderRadius="full"
              textTransform="none"
              px={{ base: 2, md: 3 }}
              fontWeight="normal"
              whiteSpace="nowrap"
              cursor="pointer"
              onClick={handleSelectAccounts}>
              No accounts selected
            </Badge>
          )}
        {channel.status === 'connected' &&
          !(showAccountSelection && channel.selectedAccounts === null) && (
            <Badge
              fontSize={{ base: '2xs', md: 'xs' }}
              bg="background.green.200"
              color="green.700"
              borderRadius="full"
              textTransform="none"
              px={{ base: 2, md: 3 }}
              fontWeight="normal"
              whiteSpace="nowrap">
              Last Connected: {channel.lastConnected}
            </Badge>
          )}
        {channel.status === 'failed' && (
          <Badge
            fontSize={{ base: '2xs', md: 'xs' }}
            bg="background.red.500"
            color="white"
            borderRadius="full"
            textTransform="none"
            px={{ base: 2, md: 3 }}
            fontWeight="normal"
            whiteSpace="nowrap">
            Connection lost
          </Badge>
        )}
      </Flex>

      {/* Connection card */}
      <Box
        bg="surface.control"
        px={{ base: 3, md: 4 }}
        py={{ base: 3, md: 4 }}
        minHeight="64px"
        display="flex"
        borderRadius="md"
        minWidth={0}>
        <Flex
          width="full"
          alignItems="center"
          justify="space-between"
          wrap="nowrap">
          <Flex alignItems="center" minWidth={0} flex={1}>
            <Icon
              iconName={channel.icon}
              boxSize={{ base: 5, md: 6 }}
              flexShrink={0}
            />
            <Box
              ml={{ base: 3, md: 4 }}
              alignItems="center"
              minWidth={0}
              flex={1}>
              {renderFieldContent()}
            </Box>
          </Flex>
          <Flex align="center" flexShrink={0} ml={2}>
            {getRightSideContent()}
            {getActionButtons()}
          </Flex>
        </Flex>
      </Box>

      {channel.service === 'shopify' && (
        <ShopDomainModal
          isOpen={isShopDomainOpen}
          onClose={() => setIsShopDomainOpen(false)}
          onConnect={handleShopDomainConnect}
          isConnecting={isConnecting}
        />
      )}
    </Box>
  );
};

export default ConnectionItem;
